import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { SERVICES } from "@/data/services";
import SoftwareWeUse from "@/components/SoftwareWeUse";
import HeroSlider from "@/components/HeroSlider";
import PortfolioSlider from "@/components/PortfolioSlider";

const STEPS = [
  {
    code: "01",
    title: "Field Data Intake",
    desc: "Upload field notes, point files, raw instrument data, deeds, and title reports via secure transfer. We review within hours.",
  },
  {
    code: "02",
    title: "CAD Drafting",
    desc: "Licensed-surveyor-trained drafters produce layered drawings in AutoCAD / Civil 3D, aligned to your template and standards.",
  },
  {
    code: "03",
    title: "QA / QC",
    desc: "Independent reviewer validates closure, layering, scale, annotations, and jurisdiction requirements before delivery.",
  },
  {
    code: "04",
    title: "Delivery & Revisions",
    desc: "Receive .dwg + PDF + supporting files. Included revision rounds and direct line to your drafter.",
  },
];

const STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO SLIDER */}
      <HeroSlider />

      {/* TRUST BAR */}
      <section className="border-y border-zinc-200 bg-white overflow-hidden" data-testid="trust-bar">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center gap-6">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-zinc-500 whitespace-nowrap">
            Trusted by surveyors in
          </div>
          <div className="flex-1 overflow-hidden relative">
            <div className="flex gap-8 animate-marquee whitespace-nowrap">
              {[...STATES, ...STATES].map((s, i) => (
                <span key={i} className="font-mono text-sm text-zinc-700">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="bg-zinc-50 py-24 md:py-32" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
                Services · 13 Deliverables
              </div>
              <h2 className="font-display text-4xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
                Every land surveying deliverable. Drafted with precision.
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 self-end">
              <p className="text-lg text-zinc-600 leading-relaxed">
                We are a specialised CAD drafting house — land surveying only. No structural,
                no architectural distractions. Just clean drawings, on time, to your standard.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                className="group bg-white p-7 md:p-8 hover:bg-zinc-50 transition-colors"
                data-testid={`home-service-${s.slug}`}
              >
                <div className="flex items-start justify-between">
                  <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                    {String(i + 1).padStart(2, "0")} / 13
                  </span>
                  <ArrowRight size={16} weight="bold" className="text-zinc-300 group-hover:text-[#0044FF] group-hover:translate-x-1 transition-all" />
                </div>
                <h3 className="mt-6 font-display text-2xl text-zinc-950 tracking-tight leading-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
                  {s.short}
                </p>
                <div className="mt-6 pt-4 border-t border-dashed border-zinc-200 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-500 group-hover:text-[#0044FF]">
                  View documents & scope →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="bg-white py-24 md:py-32 border-y border-zinc-200" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              Our Process
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05]">
              Four steps, zero surprises.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-200 border border-zinc-200">
            {STEPS.map((st) => (
              <div key={st.code} className="bg-white p-8 relative min-h-[260px] flex flex-col">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                  Step
                </div>
                <div className="font-display text-6xl text-zinc-950 tracking-tighter leading-none mt-2">
                  {st.code}
                </div>
                <h3 className="mt-6 text-lg font-medium text-zinc-950">
                  {st.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-600 leading-relaxed flex-1">
                  {st.desc}
                </p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link
              to="/process"
              className="inline-flex items-center gap-2 text-sm font-medium text-[#0044FF] hover:text-[#0033CC]"
              data-testid="home-process-link"
            >
              Read the full workflow <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      {/* PORTFOLIO SLIDER */}
      <PortfolioSlider />

      {/* SOFTWARE WE USE */}
      <SoftwareWeUse />

      {/* USA coverage */}
      <section className="bg-zinc-950 text-white py-24 md:py-32" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#66A8FF] mb-4">
              Coverage · All 50 States
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
              From Anchorage to Key West. Every jurisdiction, drafted right.
            </h2>
            <p className="mt-6 text-zinc-400 leading-relaxed max-w-lg">
              Our drafters are versed in state-specific standards — from Texas GLO calls
              to New England&apos;s rod-and-link descriptions. We adapt to your jurisdiction,
              not the other way around.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-2 px-5 py-3 bg-white text-zinc-950 font-medium btn-lift"
              data-testid="coverage-cta"
            >
              Tell us your state <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
          <div className="border border-zinc-800 p-6 bg-zinc-900">
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.25em] font-mono text-zinc-500 pb-4 mb-4 border-b border-dashed border-zinc-700">
              <span>USA · 50 States Grid</span>
              <span>FIG.02</span>
            </div>
            <div className="grid grid-cols-10 gap-1.5">
              {STATES.map((s) => (
                <div
                  key={s}
                  className="aspect-square border border-zinc-700 hover:bg-[#0044FF] hover:border-[#0044FF] transition-colors flex items-center justify-center text-[9px] font-mono text-zinc-400 hover:text-white"
                  title={s}
                >
                  {s}
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-dashed border-zinc-700 text-xs font-mono text-zinc-500">
              50 / 50 states covered · Alaska & Hawaii included
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-white py-24 md:py-32 border-b border-zinc-200" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14">
            <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-[#0044FF] mb-4">
              What surveyors say
            </div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight text-zinc-950 leading-[1.05] max-w-3xl">
              An extension of your crew — without the overhead.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            {[
              { q: "They draft to our template perfectly. Saves us 20+ hours a week on ALTA jobs.", a: "Principal Surveyor · Texas firm" },
              { q: "First free trial sold me. Closure was tight and layering matched our internal standard on day one.", a: "PLS · Pacific Northwest" },
              { q: "Turned 1,200 raw points into a clean topo with contours in under 24 hours. Consistent quality.", a: "Project Manager · Florida civil" },
            ].map((t, i) => (
              <figure key={i} className="bg-white p-8 md:p-10">
                <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-400">Q.0{i + 1}</div>
                <blockquote className="mt-6 font-display text-xl md:text-2xl text-zinc-950 leading-snug">
                  “{t.q}”
                </blockquote>
                <figcaption className="mt-6 text-sm text-zinc-500">— {t.a}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PRE-FOOTER CTA (distinct from related services) */}
      <section className="bg-[#0044FF] text-white py-20 md:py-28" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-10 items-center">
          <h2 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Send us your next job. First drawing is on us.
          </h2>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link
              to="/free-trial"
              className="px-6 py-4 bg-white text-[#0044FF] font-medium btn-lift"
              data-testid="cta-free-trial-home"
            >
              Start Free Trial
            </Link>
            <Link
              to="/contact"
              className="px-6 py-4 border border-white text-white font-medium btn-lift hover:bg-white/10"
              data-testid="cta-contact-home"
            >
              Talk to us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
